import { trackConfig, type IFramework } from '@/framework';
import { LayerType, type ILayerProps, type IVectorLayerProps, type VectorTextInfo } from '@/version-types';
import { pushHistory, updateLayer } from '../utils';

export interface ITextColor {
    /**
     * 设置文本颜色
     *
     * @param layerID - 图层 ID
     * @param color - 颜色
     * @param options - 可选；配置选项；`options.writeHistory` 是否写入历史记录
     */
    setTextColor(layerID: string, color: string, options?: { writeHistory: boolean }): ILayerProps | void;

    /**
     * 设置文本透明度
     *
     * @param layerID - 图层 ID
     * @param opacity - 透明度
     * @param options - 可选；配置选项；`options.writeHistory` 是否写入历史记录
     */
    setTextOpacity(layerID: string, opacity: number, options?: { writeHistory?: boolean }): ILayerProps | void;
}

export class TextColor implements ITextColor {
    private prevLayer: ILayerProps | null = null;

    constructor(private framework: IFramework) {}

    private set(layerID: string, kind: 'color', value: string, options?: { writeHistory: boolean }): ILayerProps | void;
    private set(
        layerID: string,
        kind: 'opacity',
        value: number,
        options?: { writeHistory: boolean },
    ): ILayerProps | void;
    private set(layerID: string, kind: 'color' | 'opacity', value: string | number, options = { writeHistory: true }) {
        const prevLayer = (() => {
            if (this.prevLayer) return this.prevLayer;
            this.prevLayer = this.framework.getLayer(layerID, true);
            return this.prevLayer;
        })();
        if (!prevLayer || prevLayer.layerType !== LayerType.VECTOR_LAYER) return;

        const layerData = prevLayer.data as IVectorLayerProps;
        const { textInfo } = layerData;

        if (!textInfo) return;

        const { writeHistory } = options;

        if (writeHistory) {
            const nextLayer = this.framework.getLayer(layerID, true);

            pushHistory(this.framework, prevLayer, nextLayer, trackConfig[LayerType.TEXT_LAYER][kind]);
            this.prevLayer = null;

            return nextLayer;
        }

        const nextTextInfo: VectorTextInfo = { ...textInfo, [kind]: value };
        const nextLayerData: IVectorLayerProps = { ...layerData, textInfo: nextTextInfo };
        const nextLayer: ILayerProps = { ...prevLayer, data: nextLayerData };

        updateLayer(this.framework, nextLayer);

        return nextLayer;
    }

    setTextColor(layerID: string, color: string, options = { writeHistory: true }) {
        return this.set(layerID, 'color', color, options);
    }

    setTextOpacity(layerID: string, opacity: number, options = { writeHistory: true }) {
        return this.set(layerID, 'opacity', opacity, options);
    }
}
